import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FieldLabelRowComponent } from './field-label-row.component';

@Component({
  selector: 'formly-field-number',
  template: `
    <mat-form-field class="dd-compact-field w-full" [class.mat-form-field-invalid]="showError">
      @if (props.label) {
      <mat-label>
        <app-field-label-row [label]="props.label" [required]="props.required"></app-field-label-row>
      </mat-label>
      }
      @if (props.addonLeft) {
      <span class="text-md text-gray-600 px-2 inline-block" matPrefix>
        @if (props.addonLeft.icon) {
        <mat-icon>{{ props.addonLeft.icon }}</mat-icon>
        }
        @if (props.addonLeft.text) {
        <span>{{ props.addonLeft.text }}</span>
        }
      </span>
      }
      <input
        matInput
        [id]="field.id"
        type="number"
        [formControl]="formControl"
        [required]="props.required"
        [formlyAttributes]="field"
        [formlyDataCy]="field"
        [placeholder]="props.placeholder || ''"
        (keydown)="onKeyDown($event)"
        (paste)="onPaste($event)"
        [step]="props.step || 1"
        [min]="props.min || 0"
        [max]="props.max"
        (blur)="onBlur()" />
      @if (props.addonRight) {
      <span class="text-md text-gray-600 px-2 inline-block" matSuffix>
        @if (props.addonRight.icon) {
        <mat-icon>{{ props.addonRight.icon }}</mat-icon>
        }
        @if (props.addonRight.text) {
        <span>{{ props.addonRight.text }}</span>
        }
      </span>
      }

      @if (field.props.description) {
      <mat-hint>{{ field.props.description }}</mat-hint>
      }
      @if (showError) {
      <mat-error>{{ errorMessage }}</mat-error>
      }
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormlyModule,
    MatIcon,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldCustomNumberComponent extends FieldType<FieldTypeConfig> implements OnInit {
  previousValue: number;
  // Only allow numbers, backspace, arrow keys, tab, and delete
  private readonly allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    '-',
    '.',
    ',',
  ];

  ngOnInit() {
    // Manually trigger validation on init if needed
    if (this.props.required && !this.formControl.value) {
      this.formControl.markAsTouched();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Allow keyboard shortcuts (e.g., Ctrl+C, Ctrl+V)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Check if the key is allowed
    if (!this.allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }

    // Special handling for minus sign
    if (event.key === '-') {
      this.previousValue = this.formControl.value;
      requestAnimationFrame(() => {
        if (this.formControl.value == null) {
          this.formControl.setValue(this.previousValue);
        }
      });
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText) return;

    // Clean the pasted text
    const cleanedText = pastedText
      .replace(/[^\d.-]/g, '') // Remove non-numeric characters
      .replace(/-/g, '') // Remove all minus signs
      .replace(/\./g, ''); // Remove all decimal points

    // Add minus sign if original text started with it
    const value = pastedText.startsWith('-') ? '-' + cleanedText : cleanedText;

    // Insert at cursor position
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = this.formControl.value || '';

    const newValue = currentValue.toString().substring(0, start) + value + currentValue.toString().substring(end);

    this.formControl.setValue(newValue);
    this.formControl.markAsDirty();
    this.formControl.updateValueAndValidity();
  }

  onBlur(): void {
    if (this.formControl.value === null || this.formControl.value === undefined) {
      this.formControl.markAsTouched();
      return;
    }

    // Convert to number to ensure proper formatting
    const numValue = Number(this.formControl.value);
    if (!isNaN(numValue)) {
      this.formControl.setValue(numValue);
      // Check min/max constraints
      if (this.props.min !== undefined && numValue < this.props.min) {
        this.formControl.setErrors({
          ...this.formControl.errors,
          min: {
            min: this.props.min,
            actual: numValue,
          },
        });
      }
      if (this.props.max !== undefined && numValue > this.props.max) {
        this.formControl.setErrors({
          ...this.formControl.errors,
          max: {
            max: this.props.max,
            actual: numValue,
          },
        });
      }
    } else {
      this.formControl.setErrors({
        ...this.formControl.errors,
        number: true,
      });
    }

    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();
  }

  // Helper properties to get error state and message
  get showError(): boolean {
    return this.formControl.invalid && this.formControl.dirty;
  }

  get errorMessage(): string {
    if (!this.formControl.errors) {
      return '';
    }

    if (this.formControl.errors['required']) {
      return 'Dit veld is verplicht';
    }

    if (this.formControl.errors['min']) {
      return `Minimale waarde is ${this.props.min || 0}`;
    }

    if (this.formControl.errors['max']) {
      return `Maximale waarde is ${this.props.max}`;
    }

    if (this.formControl.errors['number']) {
      return 'Voer een geldig nummer in';
    }

    return 'Ongeldig invoer';
  }
}
